import { createAction } from '@reduxjs/toolkit';
import { ErrorHandlingService } from '../../api/ErrorHandlingService';
import { get, set } from 'lodash';

// T DATA L PARAMS OF PROMISE K STATE
export const createActionSync = ({
  property,
  prefix,
  promiseMethod,
  options,
}) => {
  options.additionalActions = options.additionalActions || {};
  options.setIsRefreshing = options.setIsRefreshing ?? true;
  const action_init = createAction(prefix !== '' ? `${prefix}_INIT` : 'INIT');
  const action_success = createAction(
    prefix !== '' ? `${prefix}_SUCCESS` : 'SUCCESS',
  );
  const action_added_modified = createAction(
    prefix !== '' ? `${prefix}_ADDED_MODIFIED` : 'ADDED_MODIFIED',
  );
  const action_deleted = createAction(
    prefix !== '' ? `${prefix}_DELETED` : 'DELETED',
  );
  const action_fail = createAction(prefix !== '' ? `${prefix}_FAIL` : 'FAIL');
  const action_reset = createAction(
    prefix !== '' ? `${prefix}_RESET` : 'RESET',
  );
  const initialState = (() => {
    const initial = {};
    const initialDataNormal = {
      status: 'idle',
      error: null,
      data: null,
      unsubscribe: null,
      isLoading: false,
      isError: false,
    };
    const initialDataMutation = {
      status: 'idle',
      error: null,
      isError: false,
      isLoading: false,
    };
    const initialData =
      options.typeOfQuery === 'mutation'
        ? initialDataMutation
        : initialDataNormal;
    set(initial, property, initialData);
    return property !== '' ? initial : initialData;
  })();
  const actions = {
    action_init,
    action_success,
    action_fail,
    action_reset,
    ...(options.typeOfQuery === 'all' || options.typeOfQuery === 'single'
      ? { action_added_modified }
      : {}),
    ...(options.typeOfQuery === 'all' || options.typeOfQuery === 'single'
      ? { action_deleted }
      : {}),
  };
  const actionsArray = Object.values(actions);
  const additionalSetters =
    options.additionalSetters && options.additionalSetters(prefix);
  const reducers = {
    [action_init.type]: (state, action) => {
      const _property =
        action.payload.id && options.isDynamicId
          ? `${property}.${action.payload.id}`
          : property;
      additionalSetters && additionalSetters[action_init.type]?.(state);
      if (
        get(state, _property !== '' ? `${_property}.status` : 'status') ===
          'succeeded' &&
        options.setIsRefreshing &&
        options.typeOfQuery !== 'mutation'
      ) {
        set(
          state,
          _property !== '' ? `${_property}.isRefreshing` : 'isRefreshing',
          true,
        );
        set(
          state,
          _property !== '' ? `${_property}.status` : 'status',
          'refreshing',
        );
      } else {
        set(
          state,
          _property !== '' ? `${_property}.status` : 'status',
          'pending',
        );
        set(
          state,
          _property !== '' ? `${_property}.isLoading` : 'isLoading',
          true,
        );
      }
      set(state, _property !== '' ? `${_property}.isError` : 'isError', false);
    },
    [action_success.type]: (state, action) => {
      const _property =
        action.payload.customId && options.isDynamicId
          ? `${property}.${action.payload.customId}`
          : property;
      additionalSetters &&
        additionalSetters[action_success.type]?.(state, action);
      set(
        state,
        _property !== '' ? `${_property}.status` : 'status',
        'succeeded',
      );
      set(
        state,
        _property !== '' ? `${_property}.isLoading` : 'isLoading',
        false,
      );
      set(
        state,
        _property !== '' ? `${_property}.isRefreshing` : 'isRefreshing',
        false,
      );
      if (options.typeOfQuery !== 'mutation') {
        if (
          action.payload &&
          typeof action.payload === 'object' &&
          'unsubscribe' in action.payload
        ) {
          set(
            state,
            _property !== '' ? `${_property}.unsubscribe` : 'unsubscribe',
            action.payload.unsubscribe,
          );
          set(
            state,
            _property !== '' ? `${_property}.changeListener` : 'changeListener',
            action.payload.changeListener,
          );
          set(
            state,
            _property !== '' ? `${_property}.data` : 'data',
            action.payload.data,
          );
          return;
        }
        set(
          state,
          _property !== '' ? `${_property}.data` : 'data',
          action.payload,
        );
      }
    },
    [action_added_modified.type]: (state, action) => {
      const _property =
        action.payload.customId && options.isDynamicId
          ? `${property}.${action.payload.customId}`
          : property;
      additionalSetters &&
        additionalSetters[action_added_modified.type]?.(state, action);
      if (options.typeOfQuery === 'all') {
        const data = get(
          state,
          _property !== '' ? `${_property}.data` : 'data',
          [],
        );
        const index = data.findIndex((item) => item.id === action.payload.id);
        if (index >= 0) {
          set(
            state,
            _property !== '' ? `${_property}.data[${index}]` : `data[${index}]`,
            action.payload,
          );
          return;
        }
        if (action.payload.index !== undefined) {
          const { index: _, ...rest } = action.payload;
          data.splice(_, 0, rest);
        } else {
          data.push(action.payload);
        }
        set(state, _property !== '' ? `${_property}.data` : 'data', data);
        return;
      }
      if (options.typeOfQuery === 'single') {
        const oldData = get(
          state,
          _property !== '' ? `${_property}.data` : 'data',
          {},
        );
        set(state, _property !== '' ? `${_property}.data` : 'data', {
          ...oldData,
          ...action.payload,
        });
      }
    },
    [action_deleted.type]: (state, action) => {
      const _property =
        action.payload.customId && options.isDynamicId
          ? `${property}.${action.payload.customId}`
          : property;
      additionalSetters &&
        additionalSetters[action_deleted.type]?.(state, action);
      const data = get(
        state,
        _property !== '' ? `${_property}.data` : 'data',
        [],
      );
      const index = data.findIndex((item) => item.id === action.payload.id);
      if (index >= 0) {
        data.splice(index, 1);
        set(state, _property !== '' ? `${_property}.data` : 'data', data);
      }
    },
    [action_fail.type]: (state, action) => {
      const _property =
        action.payload.id && options.isDynamicId
          ? `${property}.${action.payload.id}`
          : property;
      additionalSetters && additionalSetters[action_fail.type]?.(state, action);
      set(state, _property !== '' ? `${_property}.status` : 'status', 'error');
      set(
        state,
        _property !== '' ? `${_property}.error` : 'error',
        action.payload.error,
      );
      set(
        state,
        _property !== '' ? `${_property}.isLoading` : 'isLoading',
        false,
      );
      set(state, _property !== '' ? `${_property}.isError` : 'isError', true);
    },
    [action_reset.type]: (state, action) => {
      const _property =
        action.payload.id && options.isDynamicId
          ? `${property}.${action.payload.id}`
          : property;
      additionalSetters && additionalSetters[action_reset.type]?.(state);
      const unsubscribe = get(
        state,
        _property !== '' ? `${_property}.unsubscribe` : 'unsubscribe',
        () => {
          return;
        },
      );
      if (unsubscribe) unsubscribe();

      set(
        state,
        _property !== '' ? `${_property}.changeListener` : 'changeListener',
        undefined,
      );

      set(state, _property !== '' ? `${_property}.data` : 'data', null);
      set(state, _property !== '' ? `${_property}.error` : 'error', null);
      set(
        state,
        _property !== '' ? `${_property}.unsubscribe` : 'unsubscribe',
        null,
      );
      set(state, _property !== '' ? `${_property}.status` : 'status', 'idle');
      set(
        state,
        _property !== '' ? `${_property}.isLoading` : 'isLoading',
        false,
      );
    },
  };
  const asyncExecuter =
    (params) =>
    async ({ dispatch, state, id }) => {
      const modified = (data) => {
        dispatch(
          action_added_modified({
            ...data,
            ...(options.isDynamicId ? { customId: id } : {}),
          }),
        );
      };
      const deleted = (data) => {
        dispatch(
          action_deleted({
            id: data,
            ...(options.isDynamicId ? { customId: id } : {}),
          }),
        );
      };
      const reset = () => {
        dispatch(action_reset({ id }));
      };
      try {
        dispatch(action_init({ id }));
        const data = await promiseMethod({
          modified,
          deleted,
          reset,
          actions,
          actionsArray,
          dispatch,
          state,
          params,
        });
        if (data) {
          dispatch(
            action_success({
              ...data,
              ...(options.isDynamicId ? { customId: id } : {}),
            }),
          );
        }
      } catch (error) {
        ErrorHandlingService.handleError(error, prefix, property);
        dispatch(action_fail({ id, error: error || error.toString() }));
      }
    };
  const setAllBuilder = (builder) => {
    const combinedReducers = { ...reducers, ...options.additionalActions };
    Object.keys({ ...reducers, ...options.additionalActions }).forEach(
      (key) => {
        builder.addCase(key, combinedReducers[key]);
      },
    );
  };
  const resetAll =
    () =>
    ({ dispatch, id }) => {
      dispatch(action_reset({ id }));
    };
  return {
    actions,
    actionsArray,
    reducers,
    initialState,
    asyncExecuter,
    setAllBuilder,
    resetAll,
  };
};
